// src/DevStreamOrgSelector.js
import React, { useState, useEffect } from "react";
import logger from "../../utils/logger";
import "./DevStream.css";

// Special organization IDs for Full Admin filtering
const SPECIAL_ORG_ALL = "ALL_ORGS";
const SPECIAL_ORG_ORPHANED = "ORPHANED";

/**
 * DevStreamOrgSelector - Organization dropdown for Dev Streams
 *
 * Features:
 * - Shows "Organization: [Name (ID)]" format
 * - Accepts organizations array as prop (preferred) or falls back to localStorage
 * - Allows switching between organizations
 * - Triggers event clearing and WebSocket org update on change
 * - For Full Admins: Shows special options to view all events or orphaned events
 */
const DevStreamOrgSelector = ({
  selectedOrgId,
  onOrganizationChange,
  isFullAdmin = false,
  organizations: propOrganizations, // Accept organizations as prop
}) => {
  const [organizations, setOrganizations] = useState([]);
  const [retryCount, setRetryCount] = useState(0);

  // Get organizations from localStorage (cached by main app) - FALLBACK ONLY
  const getOrganizationsFromCache = () => {
    try {
      const cachedOrgs = localStorage.getItem("orgs_cache");
      logger.log(
        "[DevStreamOrgSelector] Reading orgs_cache (fallback):",
        cachedOrgs ? `${cachedOrgs.substring(0, 100)}...` : "null"
      );

      if (cachedOrgs) {
        const parsed = JSON.parse(cachedOrgs);
        if (Array.isArray(parsed)) {
          logger.log(
            "[DevStreamOrgSelector] Found",
            parsed.length,
            "organizations in cache"
          );
          // Sort organizations alphabetically by name
          return parsed.sort((a, b) =>
            (a.name || "").localeCompare(b.name || "")
          );
        }
      }
    } catch (e) {
      logger.error("[DevStreamOrgSelector] Failed to parse cached orgs", e);
    }
    logger.warn("[DevStreamOrgSelector] No organizations found in cache");
    return [];
  };

  // Use prop organizations if available, otherwise fall back to cache with retry
  useEffect(() => {
    if (propOrganizations && Array.isArray(propOrganizations)) {
      logger.log(
        "[DevStreamOrgSelector] Using organizations from props:",
        propOrganizations.length
      );
      // Sort organizations alphabetically by name
      const sorted = [...propOrganizations].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
      setOrganizations(sorted);
      return; // Skip cache fallback
    }

    // FALLBACK: No prop organizations, try localStorage cache
    const orgs = getOrganizationsFromCache();
    setOrganizations(orgs);

    // If no orgs found and we haven't retried too many times, try again
    if (orgs.length === 0 && retryCount < 5) {
      const timer = setTimeout(() => {
        logger.log(
          "[DevStreamOrgSelector] Retrying org cache read (fallback), attempt",
          retryCount + 1
        );
        setRetryCount(retryCount + 1);
      }, 500); // Retry after 500ms

      return () => clearTimeout(timer);
    }
  }, [propOrganizations, retryCount]);

  // Handle organization selection
  const handleChange = (e) => {
    const newOrgId = e.target.value;
    if (newOrgId !== selectedOrgId) {
      onOrganizationChange(newOrgId);
    }
  };

  // Get display name for selected organization (including special ones)
  const getDisplayName = (orgId) => {
    if (orgId === SPECIAL_ORG_ALL) {
      return "Events for All Organizations";
    }
    if (orgId === SPECIAL_ORG_ORPHANED) {
      return "Orphaned Events (No Org Defined/Access)";
    }
    const org = organizations.find((o) => o.orgId === orgId);
    return org ? `${org.name} (${org.orgId})` : "Select organization";
  };

  if (organizations.length === 0 && !isFullAdmin) {
    return (
      <div className="dev-stream-org-selector">
        <label className="org-selector-label">Organization:</label>
        <div className="org-selector-error">No organizations available</div>
      </div>
    );
  }

  return (
    <div className="dev-stream-org-selector">
      <label className="org-selector-label">Organization:</label>
      <select
        className="org-selector-dropdown"
        value={selectedOrgId || ""}
        onChange={handleChange}
        title={getDisplayName(selectedOrgId)}
      >
        {!selectedOrgId && (
          <option value="" disabled>
            Select organization...
          </option>
        )}

        {/* Special options for Full Admin only */}
        {isFullAdmin && (
          <>
            <option value={SPECIAL_ORG_ALL}>
              🌐 Events for All Organizations
            </option>
            <option value={SPECIAL_ORG_ORPHANED}>
              ⚠️ Orphaned Events (No Org Defined/Access)
            </option>
            {organizations.length > 0 && (
              <option disabled>──────────────────────</option>
            )}
          </>
        )}

        {/* Regular organizations */}
        {organizations.map((org) => (
          <option key={org.orgId} value={org.orgId}>
            {org.name} ({org.orgId})
          </option>
        ))}
      </select>
    </div>
  );
};

export default DevStreamOrgSelector;
